/**
 * auth.js — Authentication module (Clerk).
 *
 * Uses Clerk (@clerk/clerk-js) for sign-in / session management.
 *
 * Flow:
 *   1. On app startup, initAuth() loads Clerk with the publishable key.
 *   2. If no active session, the sign-in overlay is shown with Clerk's
 *      <SignIn/> component mounted (Google OAuth + email/password).
 *   3. Once signed in, the overlay hides and the app becomes interactive.
 *   4. All /api requests get an `Authorization: Bearer <clerk session token>`
 *      header injected automatically by a global fetch wrapper (Clerk session
 *      tokens are short-lived and refreshed on demand via getToken()).
 *   5. On any 401 from the backend, forceLogout() shows the overlay again.
 *
 * Config: set VITE_CLERK_PUBLISHABLE_KEY in the frontend build environment
 * (webapp/.env or .env.production). See webapp/.env.example.
 */

import { Clerk } from '@clerk/clerk-js'

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

/** @type {import('@clerk/clerk-js').Clerk | null} */
let clerk = null
let _fetchPatched = false
let _xhrPatched = false

// ── Clerk prebuilt-UI bundle loader ────────────────────────────────────
// Clerk v6.25+ ships its prebuilt UI components (SignIn, UserButton, ...) in a
// SEPARATE bundle (@clerk/ui). It must be loaded BEFORE Clerk.load() and passed
// in as `ui.ClerkUI`, otherwise mountSignIn / mountUserButton throw
// "Clerk was not loaded with Ui components". The bundle exposes the component
// constructor on window.__internal_ClerkUICtor once its script executes.

/** Derive the Clerk Frontend API host from a publishable key. */
function _frontendApiFromKey(pk) {
  try {
    return atob(pk.split('_')[2]).replace(/\$+$/, '')
  } catch {
    return null
  }
}

/** Inject the @clerk/ui browser bundle; sets window.__internal_ClerkUICtor. */
function _loadClerkUI(pk) {
  return new Promise((resolve, reject) => {
    if (window.__internal_ClerkUICtor) return resolve()
    const fapi = _frontendApiFromKey(pk)
    if (!fapi) return reject(new Error('Invalid Clerk publishable key'))
    const script = document.createElement('script')
    script.async = true
    script.crossOrigin = 'anonymous'
    script.src = `https://${fapi}/npm/@clerk/ui@1/dist/ui.browser.js`
    script.addEventListener('load', () => resolve())
    script.addEventListener('error', () => reject(new Error('Failed to load Clerk UI bundle')))
    document.head.appendChild(script)
  })
}

// ── Token accessor (used by the fetch wrapper) ────────────────────────────

/**
 * Return a Clerk session JWT, or null if not signed in.
 * Clerk caches and transparently refreshes the token.
 * Pass through Clerk getToken options such as { skipCache: true } when a
 * request should force a fresh token fetch before treating the session as dead.
 */
export async function getToken(options = undefined) {
  try {
    if (clerk && clerk.session) return await clerk.session.getToken(options)
  } catch { /* fall through */ }
  return null
}

/**
 * Subscribe to sign-in / sign-out changes. Calls cb(isSignedIn: boolean) on
 * every future auth-state change. Returns an unsubscribe fn (no-op if Clerk
 * isn't ready yet). Register after initAuth() has resolved.
 */
export function onAuthStateChange(cb) {
  if (!clerk) return () => {}
  return clerk.addListener(({ user }) => cb(!!user))
}

// ── Logout ─────────────────────────────────────────────────────────────────

export function forceLogout() {
  if (clerk) clerk.signOut().catch(() => {})
  _showOverlay()
  _mountSignIn()
}

// ── DOM helpers ────────────────────────────────────────────────────────────

function _showOverlay() {
  const overlay = document.getElementById('auth-overlay')
  if (!overlay) return
  overlay.classList.remove('auth-hidden')
  overlay.classList.add('auth-visible')
}

function _hideOverlay() {
  const overlay = document.getElementById('auth-overlay')
  if (!overlay) return
  overlay.classList.remove('auth-visible')
  overlay.classList.add('auth-hidden')
}

function _setError(msg) {
  const el = document.getElementById('auth-error')
  if (el) el.textContent = msg
}

// ── Clerk component mounts ────────────────────────────────────────────────

function _mountSignIn() {
  if (!clerk) return
  const el = document.getElementById('clerk-signin')
  if (el) { el.innerHTML = ''; clerk.mountSignIn(el) }
  // Mobile layout hides the inline card and shows a button that opens Clerk.
  const btn = document.getElementById('auth-mobile-signin')
  if (btn) btn.onclick = () => {
    try { clerk.openSignIn({}) }
    catch { try { clerk.redirectToSignIn() } catch { /* sign-in unavailable */ } }
  }
}

function _mountUserButton() {
  const el = document.getElementById('clerk-userbutton')
  if (!el || !clerk) return
  el.innerHTML = ''
  clerk.mountUserButton(el, { afterSignOutUrl: window.location.origin })
}

// ── Global fetch wrapper — injects the bearer token on /api requests ──────

function _installFetchAuth() {
  if (_fetchPatched) return
  _fetchPatched = true
  const orig = window.fetch.bind(window)
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : (input && input.url) || ''
    if (url.startsWith('/api') && !url.startsWith('/api/health')) {
      const token = await getToken()
      if (token) {
        const headers = new Headers(
          (init && init.headers) ||
          (typeof input !== 'string' && input.headers) ||
          undefined,
        )
        headers.set('Authorization', `Bearer ${token}`)
        init = { ...init, headers }
      }
    }
    return orig(input, init)
  }
}

// ── Global XMLHttpRequest wrapper ─ injects the bearer token on /api requests ─
// Some flows (e.g. the large-file Bluebeam upload check) use XHR for upload
// progress and would otherwise bypass the fetch wrapper above, yielding a 401.

function _installXhrAuth() {
  if (_xhrPatched) return
  _xhrPatched = true
  const OrigOpen = XMLHttpRequest.prototype.open
  const OrigSend = XMLHttpRequest.prototype.send
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__authUrl = typeof url === 'string' ? url : (url && url.toString()) || ''
    return OrigOpen.call(this, method, url, ...rest)
  }
  XMLHttpRequest.prototype.send = function (body) {
    if (this.__skipAuthHeader) return OrigSend.call(this, body)
    const url = this.__authUrl || ''
    if (url.startsWith('/api') && !url.startsWith('/api/health')) {
      getToken(this.__authTokenOptions)
        .then((token) => {
          try { if (token) this.setRequestHeader('Authorization', `Bearer ${token}`) } catch { /* header set failed */ }
          OrigSend.call(this, body)
        })
        .catch(() => OrigSend.call(this, body))
      return
    }
    return OrigSend.call(this, body)
  }
}

// ── Public init ────────────────────────────────────────────────────────────

/**
 * Call once on app startup. Loads Clerk, shows the sign-in overlay unless a
 * session already exists, and resolves when the user is authenticated.
 */
export async function initAuth() {
  if (!PUBLISHABLE_KEY) {
    _setError('Authentication is not configured (missing Clerk publishable key).')
    _showOverlay()
    // Block the app: without a key we cannot authenticate.
    return new Promise(() => {})
  }

  await _loadClerkUI(PUBLISHABLE_KEY)
  clerk = new Clerk(PUBLISHABLE_KEY)
  await clerk.load({ ui: { ClerkUI: window.__internal_ClerkUICtor } })

  _installFetchAuth()
  _installXhrAuth()
  _mountUserButton()

  // React to auth state changes (sign-in / sign-out).
  clerk.addListener(({ user }) => {
    if (user) {
      _hideOverlay()
    } else {
      _showOverlay()
      _mountSignIn()
      _mountUserButton()
    }
  })

  if (clerk.user) {
    _hideOverlay()
    return
  }

  _showOverlay()
  _mountSignIn()

  return new Promise((resolve) => {
    const unsub = clerk.addListener(({ user }) => {
      if (user) {
        _hideOverlay()
        if (typeof unsub === 'function') unsub()
        resolve()
      }
    })
  })
}
